import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast, formatNumInput, parseNumInput, reverseSaleEffects, decrementVisitIfEmpty, getGrade } from '../../lib/utils';
import { STORE_NAMES, STORE_MAP } from '../../lib/constants';

export default function MgrSalesViewPage({ profile }) {
  // 본사 계정(admin/hq — 매니저 아님)은 점포/지점 선택해서 조회 + 라인 수정 가능
  const isStoreMgr = profile?.job_title === '매니저';
  const isHQ = !isStoreMgr;
  const [hqStore,  setHqStore]  = useState('');
  const [hqBranch, setHqBranch] = useState('');
  const storeName  = isStoreMgr ? profile.department : hqStore;
  const branchName = isStoreMgr ? profile.branch     : hqBranch;
  const hqBranchOptions = useMemo(() => hqStore ? (STORE_MAP[hqStore] || []) : [], [hqStore]);

  const today = new Date();
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const todayStr = fmt(today);
  const monthStart = `${today.getFullYear()}-${pad(today.getMonth()+1)}-01`;

  // 수정 권한: 매장·본사 모두 전체 기간 수정 가능 (수량·합계)
  const curMonth = `${today.getFullYear()}-${pad(today.getMonth()+1)}`;
  const isCurrentMonth = (sold_at) => String(sold_at || '').slice(0, 7) === curMonth;
  const canEditLine = () => true;

  const [fFrom, setFFrom] = useState(monthStart);
  const [fTo,   setFTo]   = useState(todayStr);
  const [sales, setSales] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);
  const [expandedTxn, setExpandedTxn]   = useState(null);  // 트랜잭션 그룹 식별자(첫 라인의 id)

  // 본사 계정용 라인 인라인 수정
  const [editingLine, setEditingLine] = useState(null);  // sale id
  const [editDraft,   setEditDraft]   = useState({});    // { quantity, price, payment, memo }
  const [savingLine,  setSavingLine]  = useState(false);
  const [selDel,      setSelDel]      = useState(new Set()); // 본사 선택삭제 — sale row id 집합
  const [deletingSel, setDeletingSel] = useState(false);

  const startEditLine = (it) => {
    setEditingLine(it.id);
    setEditDraft({
      quantity: it.quantity,
      price:    it.price,
      total:    Math.round(Number(it.price || 0) * Number(it.quantity || 0)), // 합계 = 단가 × 수량
      payment:  it.payment || '카드',
      memo:     it.memo || '',
    });
  };
  const cancelEditLine = () => { setEditingLine(null); setEditDraft({}); };
  const saveEditLine = async (it) => {
    const qty = Number(editDraft.quantity);
    if (!Number.isFinite(qty) || qty === 0) { toast('수량은 0이 아닌 숫자여야 합니다', 'err'); return; }
    let price;
    if (isHQ) {
      price = Number(parseNumInput(editDraft.price));
      if (!Number.isFinite(price)) { toast('단가가 유효하지 않습니다', 'err'); return; }
    } else {
      // 매장: 합계 입력 → 단가 = 합계 ÷ 수량
      const total = Number(parseNumInput(editDraft.total));
      if (!Number.isFinite(total) || total < 0) { toast('합계가 유효하지 않습니다', 'err'); return; }
      if (qty < 0) { toast('수량은 0 이상이어야 합니다', 'err'); return; }
      price = qty !== 0 ? total / qty : 0;
    }
    setSavingLine(true);
    try {
      const oldQty = Number(it.quantity) || 0;
      const oldAmt = (Number(it.price) || 0) * oldQty;
      const newAmt = price * qty;

      // 회원 적립 재계산 — 이 판매 제외한 누적 기준 등급률로 재적립 (판매입력 로직과 동일)
      let custRow = null, newEarned = 0;
      if (it.customer_id) {
        const { data: cust } = await supabase.from('customers')
          .select('total_points, used_points, total_purchase, purchase_qty').eq('id', it.customer_id).single();
        custRow = cust || null;
        if (custRow) {
          const totalBefore = Math.max(0, (custRow.total_purchase || 0) - oldAmt);
          newEarned = Math.floor(newAmt * getGrade(totalBefore).rate);
        }
      }

      // 매장 매니저는 수량·금액만 갱신 (결제수단/메모는 본사만)
      const patch = isHQ
        ? { quantity: qty, price, payment: editDraft.payment || '카드', memo: editDraft.memo?.trim() || null }
        : { quantity: qty, price };
      if (custRow) patch.points_earned = newEarned;   // 적립분 재계산 반영 (이후 삭제 원복 정확)
      const { error } = await supabase.from('sales').update(patch).eq('id', it.id);
      if (error) throw error;

      // 재고 반영 — 수량 변화(old−new)만큼 store_stock 조정 (판매 시 차감과 동일 조건)
      const code = it.product?.code || it.product_code;
      if (qty !== oldQty && code && it.delivery_type !== 'hq') {  // 강좌매출도 재고 차감 대상이므로 포함
        const { data: stockRow } = await supabase.from('store_stock')
          .select('id, stock_qty')
          .eq('store_name', it.store_name).eq('branch_name', it.branch_name).eq('product_code', code)
          .maybeSingle();
        if (stockRow) {
          await supabase.from('store_stock')
            .update({ stock_qty: (stockRow.stock_qty || 0) + (oldQty - qty), updated_at: new Date().toISOString() })
            .eq('id', stockRow.id);
        }
      }

      // 회원 적립금·누적구매액·구매수량·등급 반영
      if (custRow) {
        const oldEarned = Number(it.points_earned) || 0;
        const newTotalPurchase = Math.max(0, (custRow.total_purchase || 0) - oldAmt + newAmt);
        await supabase.from('customers').update({
          total_purchase: newTotalPurchase,
          purchase_qty:   Math.max(0, (custRow.purchase_qty || 0) - oldQty + qty),
          total_points:   Math.max(0, (custRow.total_points || 0) - oldEarned + newEarned),
          grade:          getGrade(newTotalPurchase).grade,
        }).eq('id', it.customer_id);
      }

      toast(custRow ? '수정 완료 — 재고·적립금·누적구매 반영됨' : (qty !== oldQty ? '수정 완료 — 재고 반영됨' : '수정 완료'), 'ok');
      cancelEditLine();
      fetchSales();
    } catch (err) {
      toast('수정 실패: ' + (err.message || err), 'err');
    }
    setSavingLine(false);
  };
  // 본사 — 주문건(결제 묶음) 선택 + 일괄/선택 삭제
  const toggleTxnDel = (rows) => setSelDel(prev => {
    const ids = rows.map(x => x.id);
    const allOn = ids.every(id => prev.has(id));
    const n = new Set(prev);
    if (allOn) ids.forEach(id => n.delete(id)); else ids.forEach(id => n.add(id));
    return n;
  });
  const txnChecked = (rows) => rows.length > 0 && rows.every(x => selDel.has(x.id));
  const allSaleIds = sales.map(s => s.id);
  const allDelOn = allSaleIds.length > 0 && allSaleIds.every(id => selDel.has(id));
  const toggleAllDel = () => setSelDel(allDelOn ? new Set() : new Set(allSaleIds));
  const deleteSelected = async () => {
    const ids = [...selDel];
    if (ids.length === 0) { toast('삭제할 주문건을 선택해주세요', 'err'); return; }
    if (!window.confirm(`선택한 ${ids.length}개 라인을 영구 삭제하시겠습니까?\n\n주문(결제 묶음) 단위로 선택된 매출이 모두 삭제되며, 되돌릴 수 없습니다.`)) return;
    setDeletingSel(true);
    try {
      // 원복용 상세 조회 (삭제 전)
      const { data: details } = await supabase.from('sales')
        .select('id, customer_id, sold_at, points_earned, points_used, price, quantity, store_name, branch_name, delivery_type, payment, product:products(code)')
        .in('id', ids);
      const { data, error } = await supabase.from('sales').delete().in('id', ids).select('id');
      if (error) throw error;
      const deletedIds = new Set((data || []).map(d => d.id));
      const deleted = deletedIds.size;
      if (deleted === 0) {
        toast('삭제 권한이 없습니다 (RLS). 본사 담당자 삭제 정책이 필요합니다.', 'err');
      } else {
        // 삭제 성공한 건만 원복 (순차 — 같은 회원 여러 건 정확 누적)
        for (const s of (details || [])) {
          if (deletedIds.has(s.id)) await reverseSaleEffects(s);
        }
        // 방문(구매건수) 원복 — 같은 (회원, 날짜) 방문은 1회만 차감
        const visits = new Set();
        for (const s of (details || [])) if (deletedIds.has(s.id) && s.customer_id && s.sold_at) visits.add(`${s.customer_id}|${s.sold_at}`);
        for (const v of visits) { const [cid, sd] = v.split('|'); await decrementVisitIfEmpty(Number(cid), sd); }
        toast(`${deleted}건 삭제 완료 (적립금·재고 원복)${deleted < ids.length ? ` · 권한 없어 ${ids.length - deleted}건 제외` : ''}`, deleted < ids.length ? 'err' : 'ok');
        setSelDel(new Set());
        fetchSales();
      }
    } catch (err) {
      toast('삭제 실패: ' + (err.message || err), 'err');
    }
    setDeletingSel(false);
  };

  const deleteLine = async (it) => {
    if (!isHQ && !isCurrentMonth(it.sold_at)) { toast('당월 매출만 삭제할 수 있습니다', 'err'); return; }
    if (!window.confirm(`이 라인을 삭제하시겠습니까?\n\n상품: ${it.product?.name || '-'}\n수량: ${it.quantity}\n단가: ${Number(it.price).toLocaleString()}원\n\n삭제 시 적립금·재고·구매액(등급)이 원복됩니다. 되돌릴 수 없습니다.`)) return;
    setSavingLine(true);
    try {
      const { data: sale } = await supabase.from('sales')
        .select('id, customer_id, sold_at, points_earned, points_used, price, quantity, store_name, branch_name, delivery_type, payment, product:products(code)')
        .eq('id', it.id).single();
      const { data: delData, error } = await supabase.from('sales').delete().eq('id', it.id).select('id');
      if (error) throw error;
      if (!delData || delData.length === 0) { toast('삭제 권한이 없습니다 (RLS)', 'err'); setSavingLine(false); return; }
      await reverseSaleEffects(sale);
      await decrementVisitIfEmpty(sale?.customer_id, sale?.sold_at);
      toast('라인 삭제 완료 (적립금·재고 원복)', 'inf');
      cancelEditLine();
      fetchSales();
    } catch (err) {
      toast('삭제 실패: ' + (err.message || err), 'err');
    }
    setSavingLine(false);
  };

  // 본사 반려된 본사택배 건 → 매장발송으로 전환 + 매장재고 차감
  const [convertingTxn, setConvertingTxn] = useState(null);
  const convertToStore = async (rows) => {
    const targets = rows.filter(r => r.delivery_type === 'hq' && r.delivery_status === 'rejected');
    if (targets.length === 0) return;
    if (!window.confirm(`이 건을 '매장발송'으로 변경하시겠습니까?\n\n본사가 발송하지 않으므로, 해당 상품 ${targets.length}건의 수량만큼 매장 재고가 차감됩니다.`)) return;
    setConvertingTxn(rows[0].id);
    try {
      // 1) 매장발송으로 전환
      const ids = targets.map(r => r.id);
      const { error } = await supabase.from('sales').update({
        delivery_type: 'store', delivery_status: null, delivery_requested: true,
      }).in('id', ids);
      if (error) throw error;
      // 2) 매장재고 차감 (상품코드 기준)
      for (const r of targets) {
        const code = r.product?.code;
        if (!code) continue;
        const { data: stockRow } = await supabase.from('store_stock')
          .select('id, stock_qty')
          .eq('store_name', r.store_name).eq('branch_name', r.branch_name)
          .eq('product_code', code).maybeSingle();
        if (stockRow) {
          await supabase.from('store_stock').update({
            stock_qty: (stockRow.stock_qty||0) - (Number(r.quantity)||0),  // 음수 허용(재고 부족분 마이너스 표시)
            updated_at: new Date().toISOString(),
          }).eq('id', stockRow.id);
        }
      }
      toast('매장발송으로 변경 완료 — 매장 재고 차감됨', 'ok');
      fetchSales();
    } catch (err) {
      toast('변경 실패: ' + (err.message || err), 'err');
    }
    setConvertingTxn(null);
  };

  const setRange = (type) => {
    if (type === 'today') {
      setFFrom(todayStr); setFTo(todayStr);
    } else if (type === 'yesterday') {
      const y = new Date(today); y.setDate(today.getDate()-1);
      setFFrom(fmt(y)); setFTo(fmt(y));
    } else if (type === 'thismonth') {
      setFFrom(monthStart); setFTo(todayStr);
    } else if (type === 'lastmonth') {
      const first = new Date(today.getFullYear(), today.getMonth()-1, 1);
      const last  = new Date(today.getFullYear(), today.getMonth(), 0);
      setFFrom(fmt(first)); setFTo(fmt(last));
    }
  };

  const fetchSales = useCallback(async () => {
    if (!fFrom || !fTo) { toast('기간을 선택해주세요', 'err'); return; }
    if (!storeName || !branchName) {
      setSales([]); setLoading(false); return;
    }
    setLoading(true);
    setExpandedDate(null);
    const { data, error } = await supabase.from('sales')
      .select('*, brand:brands(name), product:products(name,code), customer:customers(name,phone)')
      .neq('payment', '구매이력')
      .eq('store_name', storeName)
      .eq('branch_name', branchName)
      .gte('sold_at', fFrom).lte('sold_at', fTo)
      .order('sold_at', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) toast(error.message, 'err');
    // 새 정책: 반품은 음수 매출 row로 처리되므로 returned_qty는 무시 (이중 반품 방지용으로만 보존)
    else setSales((data || []).map(r => ({...r, _eff: (r.quantity || 0)})));
    setSelDel(new Set());
    setLoading(false);
  }, [fFrom, fTo, storeName, branchName]);

  useEffect(() => { fetchSales(); }, []); // 최초 1회 (당월)

  // 1) 트랜잭션 단위 그룹화 — 같은 sold_at + customer + created_at 60초 윈도 = 1 결제묶음
  // 2) 일자별 집계는 트랜잭션 수 기준으로 (사용자 요청: 6개 상품 1번 결제 = 판매건수 1건)
  const { dailyRows, dailyDetails, totals } = useMemo(() => {
    // 1단계: 트랜잭션 그룹
    const txnGroups = [];
    const groupMap = new Map();
    for (const r of sales) {
      if (r._eff <= 0) continue;
      const custKey = r.customer_id || `__noc_${r.id}`;
      const baseKey = `${r.sold_at}|${custKey}`;
      const ts = new Date(r.created_at || 0).getTime();
      let g = groupMap.get(baseKey);
      if (g && Math.abs(ts - g.lastTs) <= 60000) {
        g.rows.push(r); g.lastTs = ts;
      } else {
        g = { date: r.sold_at, rows: [r], firstTs: ts, lastTs: ts };
        groupMap.set(baseKey, g);
        txnGroups.push(g);
      }
    }
    // 2단계: 일자 집계 (트랜잭션 단위로 카운트)
    const dMap = new Map();
    const dDetails = {};
    let totC = 0, totQ = 0, totA = 0;
    let delC = 0, delA = 0;
    for (const g of txnGroups) {
      const d = g.date;
      const gQty = g.rows.reduce((s, r) => s + r._eff, 0);
      const gAmt = g.rows.reduce((s, r) => s + Math.round(r.price * r._eff), 0);
      if (!dMap.has(d)) dMap.set(d, { date: d, count: 0, qty: 0, amt: 0 });
      const e = dMap.get(d);
      e.count++; e.qty += gQty; e.amt += gAmt;
      if (!dDetails[d]) dDetails[d] = [];
      dDetails[d].push(g);
      totC++; totQ += gQty; totA += gAmt;
      // 택배는 라인 단위(트랜잭션 일부만 택배일 수도 있음)
      for (const r of g.rows) {
        if (r.delivery_requested) { delC++; delA += Math.round(r.price * r._eff); }
      }
    }
    const list = [...dMap.values()].sort((a,b) => b.date.localeCompare(a.date));
    return { dailyRows: list, dailyDetails: dDetails, totals: { count: totC, qty: totQ, amt: totA, deliveryCount: delC, deliveryAmt: delA } };
  }, [sales]);

  const isToday     = fFrom === todayStr   && fTo === todayStr;
  const yest = new Date(today); yest.setDate(today.getDate()-1);
  const yestStr = fmt(yest);
  const isYesterday = fFrom === yestStr    && fTo === yestStr;
  const isThisMonth = fFrom === monthStart && fTo === todayStr;
  const lmFirst = new Date(today.getFullYear(), today.getMonth()-1, 1);
  const lmLast  = new Date(today.getFullYear(), today.getMonth(), 0);
  const isLastMonth = fFrom === fmt(lmFirst) && fTo === fmt(lmLast);

  const quickBtn = (active) => ({
    height:34, padding:'0 12px', border:'2px solid', borderRadius:'var(--radius)',
    fontSize:12, fontWeight:700, cursor:'pointer',
    borderColor: active ? 'var(--accent)' : 'var(--border)',
    background:  active ? '#fff3e0' : '#fff',
    color:       active ? 'var(--accent)' : 'var(--text2)',
  });

  return (
    <div>
      {/* 필터 */}
      <div className="card">
        <div className="card-label">매출 조회</div>
        {isStoreMgr ? (
          <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12, fontFamily:'var(--mono)' }}>
            📍 {profile.department} · {profile.branch}
          </div>
        ) : (
          <div style={{display:'flex', gap:10, marginBottom:12, flexWrap:'wrap'}}>
            <select className="fsel" value={hqStore} onChange={e => { setHqStore(e.target.value); setHqBranch(''); }}>
              <option value="">점포 선택</option>
              {STORE_NAMES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="fsel" value={hqBranch} onChange={e => setHqBranch(e.target.value)}
              disabled={!hqStore} style={{background:!hqStore?'#f0f0f0':'#fff'}}>
              <option value="">{hqStore ? '지점 선택' : '먼저 점포 선택'}</option>
              {hqBranchOptions.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {(!storeName || !branchName) && (
              <span style={{fontSize:11, color:'var(--danger)', alignSelf:'center'}}>※ 점포·지점을 선택하면 매출 조회가 시작됩니다</span>
            )}
            {(storeName && branchName) && (
              <span style={{fontSize:11, color:'#2e7d32', alignSelf:'center', fontWeight:700}}>📍 {storeName} · {branchName} (본사 — 라인 수정 가능)</span>
            )}
          </div>
        )}
        <div className="fbar" style={{flexWrap:'wrap', gap:8}}>
          <input type="date" className="fsel" value={fFrom} onChange={e => setFFrom(e.target.value)} title="시작일"/>
          <span style={{fontSize:12, color:'var(--text3)'}}>~</span>
          <input type="date" className="fsel" value={fTo} onChange={e => setFTo(e.target.value)} title="종료일"/>
          <button style={quickBtn(isToday)}     onClick={() => setRange('today')}>오늘</button>
          <button style={quickBtn(isYesterday)} onClick={() => setRange('yesterday')}>어제</button>
          <button style={quickBtn(isThisMonth)} onClick={() => setRange('thismonth')}>당월</button>
          <button style={quickBtn(isLastMonth)} onClick={() => setRange('lastmonth')}>전월</button>
          <div className="fbar-right">
            <button className="btn btn-p" onClick={fetchSales} disabled={loading}>
              {loading ? <span className="spinner"/> : '🔍 조회'}
            </button>
          </div>
        </div>
      </div>

      {/* 요약 */}
      {sales.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
          {[
            { label:'기간 매출', value: totals.amt.toLocaleString()+'원', color:'var(--accent)', big:true },
            { label:'판매 건수', value: totals.count.toLocaleString()+'건' },
            { label:'판매 수량', value: totals.qty.toLocaleString()+'개' },
            { label:'🚚 택배발송', value: totals.deliveryAmt.toLocaleString()+'원 ('+totals.deliveryCount.toLocaleString()+'건)', color:'#e65100', borderColor:'#ffcc80' },
          ].map(s => (
            <div key={s.label} style={{
              background:'#fff',
              border: s.big ? '2px solid var(--sidebar)'
                    : s.borderColor ? `2px solid ${s.borderColor}`
                    : '1px solid var(--border)',
              borderRadius:'var(--radius)', padding:'14px 18px'
            }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--text3)', marginBottom:6 }}>{s.label}</div>
              <div style={{ fontSize:s.big?22:14, fontWeight:700, color: s.color || 'var(--text)', fontFamily:'var(--mono)', lineHeight:1.3 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* 일자별 결과 */}
      {dailyRows.length === 0 && !loading && (
        <div className="empty">조회된 판매 내역이 없습니다</div>
      )}
      {dailyRows.length > 0 && (
        <div className="card" style={{padding:'16px 20px'}}>
          <div style={{marginBottom:10, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
            <span className="fresult"><b>{dailyRows.length}</b>일 / 총 <b>{totals.count.toLocaleString()}</b>건 · <b>{totals.amt.toLocaleString()}</b>원</span>
            <span style={{fontSize:11, color:'var(--text3)'}}>(날짜 행 클릭 → 주문건 펼침)</span>
            {isHQ && (
              <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:8}}>
                {selDel.size > 0 && <span style={{fontSize:12, fontWeight:700, color:'#6a1b9a'}}>선택 {selDel.size}</span>}
                <button className="btn btn-s" onClick={toggleAllDel}>{allDelOn ? '✕ 전체 해제' : '☑ 전체 선택'}</button>
                <button type="button" onClick={deleteSelected} disabled={deletingSel || selDel.size === 0}
                  title="선택한 주문건(결제 묶음) 일괄 삭제"
                  style={{height:30, padding:'0 12px', border:'1px solid var(--danger)', borderRadius:'var(--radius)', background: selDel.size>0?'#ffebee':'#f5f5f5', color: selDel.size>0?'var(--danger)':'var(--text3)', fontSize:12, fontWeight:700, cursor: selDel.size>0?'pointer':'not-allowed'}}>
                  {deletingSel ? <span className="spinner"/> : `🗑 선택 삭제${selDel.size>0?` (${selDel.size})`:''}`}
                </button>
              </div>
            )}
          </div>
          <div className="twrap">
            <table>
              <thead>
                <tr>
                  <th>날짜</th>
                  <th className="r">판매건수</th>
                  <th className="r">판매수량</th>
                  <th className="r">매출금액</th>
                  <th style={{textAlign:'center', width:90}}></th>
                </tr>
              </thead>
              <tbody>
                {dailyRows.map(r => {
                  const open = expandedDate === r.date;
                  const items = dailyDetails[r.date] || [];
                  return (
                  <React.Fragment key={r.date}>
                    <tr style={{cursor:'pointer', background: open ? '#fff8e1' : 'transparent'}}
                      onClick={() => setExpandedDate(open ? null : r.date)}>
                      <td className="mono" style={{fontWeight:700}}>{r.date}</td>
                      <td className="r">{r.count.toLocaleString()}건</td>
                      <td className="r">{r.qty.toLocaleString()}개</td>
                      <td className="r" style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--accent)'}}>{r.amt.toLocaleString()}원</td>
                      <td style={{textAlign:'center'}}>
                        <button className="btn btn-s" style={{padding:'3px 12px', fontSize:11}}
                          onClick={(e) => { e.stopPropagation(); setExpandedDate(open ? null : r.date); }}>
                          {open ? '▲ 닫기' : '▼ 조회'}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={5} style={{background:'#fafafa', padding:'10px 14px', borderTop:'2px solid var(--accent)'}}>
                          <div style={{fontSize:12, fontWeight:700, color:'var(--text2)', marginBottom:8}}>
                            📋 {r.date} 판매 내역 — {items.length}건 (결제 묶음)
                          </div>
                          <div className="twrap" style={{background:'#f4f7fb', borderRadius:'var(--radius)', border:'1px solid #cdd9e8'}}>
                            <table className="nested-table">
                              <thead>
                                <tr>
                                  <th>브랜드</th>
                                  <th>상품명</th>
                                  <th className="r">총 수량</th>
                                  <th className="r">총 합계</th>
                                  <th className="r">적립금사용</th>
                                  <th>결제</th>
                                  <th>출고방식</th>
                                  <th>고객</th>
                                  <th style={{textAlign:'center'}}>상세</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map(g => {
                                  const txnId = g.rows[0].id;
                                  const isTxnOpen = expandedTxn === txnId;
                                  const head = g.rows[0];
                                  const allFully = false; // 새 정책: 반품은 별도 음수 row, 원본은 그대로
                                  const isAllReturnEntries = g.rows.every(x => (Number(x.price)||0) < 0 || x.payment === '반품');
                                  const gQty = g.rows.reduce((s, x) => s + x._eff, 0);
                                  const gAmt = g.rows.reduce((s, x) => s + Math.round(x.price * x._eff), 0);
                                  const gPts = g.rows.reduce((s, x) => s + (Number(x.points_used)||0), 0);
                                  const hasRejected = g.rows.some(x => x.delivery_type === 'hq' && x.delivery_status === 'rejected');
                                  const rejectReason = g.rows.find(x => x.delivery_status === 'rejected' && x.delivery_reject_reason)?.delivery_reject_reason;
                                  const paymentSet = Array.from(new Set(g.rows.map(x => x.payment).filter(p => p && p !== '적립금사용')));
                                  const extraCount = g.rows.length - 1;
                                  const strike = allFully ? { textDecoration:'line-through', color:'var(--text3)' } : {};
                                  return (
                                  <React.Fragment key={txnId}>
                                  <tr style={{...(allFully?{background:'#fafafa'}:{}), ...(isTxnOpen?{background:'#fff8e1'}:{}), ...(txnChecked(g.rows)?{background:'#f3e5f5'}:{})}}>
                                    <td style={strike}>
                                      <div style={{display:'flex', alignItems:'center', gap:6}}>
                                        {isHQ && (
                                          <input type="checkbox" checked={txnChecked(g.rows)}
                                            onClick={e => e.stopPropagation()}
                                            onChange={() => toggleTxnDel(g.rows)}
                                            title="이 주문건 선택(삭제용)" style={{cursor:'pointer'}}/>
                                        )}
                                        <span>{head.brand?.name || '-'}</span>
                                      </div>
                                    </td>
                                    <td style={{fontSize:12, ...strike}}>
                                      <strong>{head.product?.name || '-'}</strong>
                                      {extraCount > 0 && (
                                        <span style={{marginLeft:6, fontSize:10, fontWeight:700, color:'#1565C0', background:'#e3f2fd', border:'1px solid #90caf9', padding:'1px 6px', borderRadius:3}}>
                                          외 {extraCount}개
                                        </span>
                                      )}
                                    </td>
                                    <td className="r" style={strike}>{gQty}</td>
                                    <td className="r" style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--accent)', ...strike}}>{gAmt.toLocaleString()}원</td>
                                    <td className="r" style={{color: gPts>0?'#6a1b9a':'var(--text3)', fontFamily:'var(--mono)', ...(allFully?{opacity:0.5}:{})}}>
                                      {gPts > 0 ? `-${gPts.toLocaleString()}` : '-'}
                                    </td>
                                    <td>
                                      {paymentSet.map(p => (
                                        <span key={p} className="badge" style={{background:'#e3f2fd',color:'#1565C0',border:'1px solid #90caf9',fontSize:11, marginRight:2, ...(allFully?{opacity:0.5}:{})}}>{p}</span>
                                      ))}
                                    </td>
                                    <td style={strike}>
                                      {(!head.delivery_type || head.delivery_type === 'none') && <span style={{fontSize:10, fontWeight:700, color:'#455a64', background:'#eceff1', border:'1px solid #b0bec5', padding:'1px 6px', borderRadius:3}}>매장판매</span>}
                                      {head.delivery_type === 'store' && <span style={{fontSize:10, fontWeight:700, color:'#e65100', background:'#fff3e0', border:'1px solid #ffcc80', padding:'1px 6px', borderRadius:3}}>택배(매장)</span>}
                                      {head.delivery_type === 'hq' && !hasRejected && <span style={{fontSize:10, fontWeight:700, color:'#e65100', background:'#fff3e0', border:'1px solid #ffcc80', padding:'1px 6px', borderRadius:3}}>택배(본사)</span>}
                                      {hasRejected && (
                                        <div onClick={e => e.stopPropagation()} style={{display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
                                          <span style={{fontSize:10, fontWeight:700, color:'var(--danger)', background:'#ffebee', border:'1px solid #ef9a9a', padding:'1px 6px', borderRadius:3}}>본사반려</span>
                                          <button type="button" onClick={() => convertToStore(g.rows)} disabled={convertingTxn === txnId}
                                            title="본사가 반려함 → 매장발송으로 변경 (매장 재고 차감)"
                                            style={{height:24, padding:'0 8px', border:'1px solid #e65100', borderRadius:4, background:'#fff3e0', color:'#e65100', fontSize:10, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                                            {convertingTxn === txnId ? '…' : '🏪 매장발송 전환'}
                                          </button>
                                          {rejectReason && <span style={{fontSize:10, color:'var(--danger)', width:'100%'}}>사유: {rejectReason}</span>}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{fontSize:12}}>{head.customer ? <span style={{color:'var(--success)',fontWeight:600}}>👤 {head.customer.name}</span> : '-'}</td>
                                    <td style={{textAlign:'center'}}>
                                      <button className="btn btn-s" style={{padding:'3px 10px', fontSize:11}}
                                        onClick={() => setExpandedTxn(isTxnOpen ? null : txnId)}>
                                        {isTxnOpen ? '▲ 닫기' : '▼ 상세'}
                                      </button>
                                    </td>
                                  </tr>
                                  {isTxnOpen && (
                                    <tr>
                                      <td colSpan={9} style={{background:'#fafafa', padding:'8px 12px', borderTop:'1px dashed var(--border)'}}>
                                        <div className="twrap" style={{background:'#eaf0f8', borderRadius:'var(--radius)', border:'1px solid #cdd9e8'}}>
                                          <table className="nested-table">
                                            <thead>
                                              <tr>
                                                <th>상품명</th>
                                                <th className="r">수량</th>
                                                <th className="r">단가</th>
                                                <th className="r">합계</th>
                                                <th className="r">적립금</th>
                                                <th>결제</th>
                                                <th>출고방식</th>
                                                <th>메모</th>
                                                <th style={{textAlign:'center', width:80}}>작업</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {g.rows.map(it => {
                                                // 새 정책: 반품 표시는 음수 매출 row 기준
                                                const fully = false;
                                                const partial = false;
                                                const eff = it._eff;
                                                const lineStrike = fully ? { textDecoration:'line-through', color:'var(--text3)' } : {};
                                                return (
                                                <tr key={it.id} style={fully?{background:'#fafafa'}:{}}>
                                                  <td style={{fontSize:12, ...lineStrike}}>
                                                    {it.product?.name || '-'}
                                                    {it.product?.code && <div style={{fontSize:10, color:'var(--text3)', fontFamily:'var(--mono)', marginTop:2}}>코드: {it.product.code}</div>}
                                                    {fully && <span style={{marginLeft:6, fontSize:10, fontWeight:700, color:'var(--danger)', background:'#fce4ec', border:'1px solid #f48fb1', padding:'1px 6px', borderRadius:3}}>반품됨</span>}
                                                    {partial && <span style={{marginLeft:6, fontSize:10, fontWeight:700, color:'#6a1b9a', background:'#f3e5f5', border:'1px solid #ce93d8', padding:'1px 6px', borderRadius:3}}>부분반품 {it.returned_qty}</span>}
                                                  </td>
                                                  <td className="r" style={lineStrike}>{eff}</td>
                                                  <td className="r" style={{fontFamily:'var(--mono)', ...lineStrike}}>{Math.round(Number(it.price)).toLocaleString()}원</td>
                                                  <td className="r" style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--accent)', ...lineStrike}}>{Math.round(it.price*eff).toLocaleString()}원</td>
                                                  <td className="r" style={{color:(it.points_used||0)>0?'#6a1b9a':'var(--text3)', fontFamily:'var(--mono)', ...(fully?{opacity:0.5}:{})}}>
                                                    {(it.points_used||0) > 0 ? `-${Number(it.points_used).toLocaleString()}` : '-'}
                                                  </td>
                                                  <td>{it.payment && it.payment !== '적립금사용' && <span className="badge" style={{background:'#e3f2fd',color:'#1565C0',border:'1px solid #90caf9',fontSize:11, ...(fully?{opacity:0.5}:{})}}>{it.payment}</span>}</td>
                                                  <td style={lineStrike}>
                                                    {(!it.delivery_type || it.delivery_type === 'none') && <span style={{fontSize:10, fontWeight:700, color:'#455a64', background:'#eceff1', border:'1px solid #b0bec5', padding:'1px 6px', borderRadius:3}}>매장판매</span>}
                                                    {it.delivery_type === 'store' && <span style={{fontSize:10, fontWeight:700, color:'#e65100', background:'#fff3e0', border:'1px solid #ffcc80', padding:'1px 6px', borderRadius:3}}>택배(매장)</span>}
                                                    {it.delivery_type === 'hq' && it.delivery_status === 'rejected' && <span style={{fontSize:10, fontWeight:700, color:'var(--danger)', background:'#ffebee', border:'1px solid #ef9a9a', padding:'1px 6px', borderRadius:3}} title={it.delivery_reject_reason ? `사유: ${it.delivery_reject_reason}` : ''}>본사반려{it.delivery_reject_reason ? ` · ${it.delivery_reject_reason}` : ''}</span>}
                                                    {it.delivery_type === 'hq' && it.delivery_status !== 'dispatched' && it.delivery_status !== 'rejected' && <span style={{fontSize:10, fontWeight:700, color:'#e65100', background:'#fff3e0', border:'1px solid #ffcc80', padding:'1px 6px', borderRadius:3}}>택배(본사)</span>}
                                                    {it.delivery_type === 'hq' && it.delivery_status === 'dispatched' && <span style={{fontSize:10, fontWeight:700, color:'#2e7d32', background:'#e8f5e9', border:'1px solid #a5d6a7', padding:'1px 6px', borderRadius:3}}>택배(본사)</span>}
                                                  </td>
                                                  <td style={{fontSize:11,color:'var(--text2)'}}>{it.memo||'-'}</td>
                                                  <td style={{textAlign:'center'}}>
                                                    {canEditLine(it) ? (
                                                      <button type="button" onClick={() => startEditLine(it)}
                                                        title={isHQ ? '라인 수정' : '매출 수정 (수량·합계)'}
                                                        style={{padding:'2px 8px', fontSize:11, border:'1px solid var(--accent)', borderRadius:4, background:'#fff3e0', color:'var(--accent)', fontWeight:700, cursor:'pointer'}}>
                                                        ✏️ 수정
                                                      </button>
                                                    ) : (
                                                      <span style={{fontSize:10, color:'var(--text3)'}} title="당월 매출만 수정 가능">–</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              )})}
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  </React.Fragment>
                                )})}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )})}
                <tr style={{background:'var(--bg3)',borderTop:'2px solid var(--border2)'}}>
                  <td style={{padding:'9px 11px',fontWeight:700}}>합계</td>
                  <td className="r" style={{fontFamily:'var(--mono)',fontWeight:700}}>{totals.count.toLocaleString()}건</td>
                  <td className="r" style={{fontFamily:'var(--mono)',fontWeight:700}}>{totals.qty.toLocaleString()}개</td>
                  <td className="r" style={{fontFamily:'var(--mono)',fontWeight:700,color:'var(--accent)',fontSize:14}}>{totals.amt.toLocaleString()}원</td>
                  <td/>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 본사 라인 수정 모달 */}
      {editingLine && (() => {
        const it = sales.find(s => s.id === editingLine);
        if (!it) return null;
        const PAYMENTS = ['카드','현금','증정','시식','반품'];
        return (
          <div style={{position:'fixed', inset:0, zIndex:400, display:'flex', alignItems:'center', justifyContent:'center'}}>
            <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={cancelEditLine}/>
            <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:'90vw', maxWidth:520, boxShadow:'0 8px 40px rgba(0,0,0,0.25)'}}>
              <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:14}}>
                <strong style={{fontSize:15}}>✏️ 매출 라인 수정</strong>
                <span style={{fontSize:11, color:'var(--text3)', fontFamily:'var(--mono)'}}>ID:{it.id} · {it.sold_at}</span>
                <button type="button" onClick={cancelEditLine}
                  style={{marginLeft:'auto', background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#999'}}>✕</button>
              </div>
              <div style={{fontSize:13, fontWeight:600, marginBottom:14}}>{it.product?.name || '-'}</div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12}}>
                <div>
                  <label style={{display:'block', fontSize:11, fontWeight:600, color:'var(--text2)', marginBottom:4}}>수량</label>
                  <input type="number" value={editDraft.quantity ?? ''}
                    onChange={e => setEditDraft(p => ({...p, quantity: e.target.value}))}
                    style={{width:'100%', height:36, padding:'0 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, textAlign:'right', fontFamily:'var(--mono)'}}/>
                </div>
                <div>
                  <label style={{display:'block', fontSize:11, fontWeight:600, color:'var(--text2)', marginBottom:4}}>{isHQ ? '단가 (원) · 음수=반품' : '합계 (원)'}</label>
                  {isHQ ? (
                    <input type="text" inputMode="numeric" value={formatNumInput(editDraft.price)}
                      onChange={e => setEditDraft(p => ({...p, price: parseNumInput(e.target.value)}))}
                      style={{width:'100%', height:36, padding:'0 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, textAlign:'right', fontFamily:'var(--mono)'}}/>
                  ) : (
                    <input type="text" inputMode="numeric" value={formatNumInput(editDraft.total)}
                      onChange={e => setEditDraft(p => ({...p, total: parseNumInput(e.target.value)}))}
                      style={{width:'100%', height:36, padding:'0 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, textAlign:'right', fontFamily:'var(--mono)', fontWeight:700, color:'var(--accent)'}}/>
                  )}
                </div>
              </div>
              {/* 결제수단/메모/삭제는 본사(HQ)만 — 매장 매니저는 당월 수량·금액만 수정 */}
              {isHQ && (
                <div style={{marginBottom:12}}>
                  <label style={{display:'block', fontSize:11, fontWeight:600, color:'var(--text2)', marginBottom:4}}>결제수단</label>
                  <div style={{display:'flex', gap:4}}>
                    {PAYMENTS.map(p => {
                      const active = editDraft.payment === p;
                      return (
                        <button key={p} type="button" onClick={() => setEditDraft(d => ({...d, payment: p}))}
                          style={{
                            flex:1, height:34, border:`1px solid ${active?'var(--accent)':'var(--border)'}`, borderRadius:6,
                            background: active ? '#fff3e0' : '#fff',
                            color:      active ? 'var(--accent)' : 'var(--text2)',
                            fontWeight: active ? 700 : 500, fontSize:12, cursor:'pointer',
                          }}>{p}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              {isHQ && (
                <div style={{marginBottom:14}}>
                  <label style={{display:'block', fontSize:11, fontWeight:600, color:'var(--text2)', marginBottom:4}}>메모</label>
                  <input type="text" value={editDraft.memo ?? ''}
                    onChange={e => setEditDraft(p => ({...p, memo: e.target.value}))}
                    style={{width:'100%', height:36, padding:'0 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:13}}/>
                </div>
              )}
              <div style={{padding:'10px 12px', background:'#fff3e0', border:'1px solid #ffcc80', borderRadius:6, fontSize:11, color:'#6d4c41', marginBottom:14}}>
                {isHQ
                  ? '⚠️ 합계 = 수량 × 단가. 반품 음수 매출은 단가에 음수 입력. 수량 변경분은 매장 재고에, 금액 변경분은 회원 적립금·누적구매·등급에 자동 반영됩니다. (적립금 사용분은 변경 안 됨)'
                  : (() => {
                      const q = Number(editDraft.quantity) || 0;
                      const t = Number(parseNumInput(editDraft.total)) || 0;
                      const unit = q !== 0 ? Math.round((t / q) * 100) / 100 : 0;
                      return `⚠️ 수량·합계를 수정합니다. 단가 = 합계 ÷ 수량 = ${unit.toLocaleString()}원. 수량 변경분은 매장 재고에, 금액 변경분은 회원 적립금·누적구매·등급에 자동 반영됩니다.`;
                    })()}
              </div>
              <div style={{display:'flex', gap:8}}>
                {(isHQ || isCurrentMonth(it.sold_at)) && (
                  <button type="button" onClick={() => deleteLine(it)} disabled={savingLine}
                    style={{height:38, padding:'0 14px', border:'1px solid var(--danger)', borderRadius:6, background:'#fff', color:'var(--danger)', fontWeight:700, fontSize:12, cursor:'pointer'}}>
                    🗑️ 삭제
                  </button>
                )}
                <button type="button" onClick={cancelEditLine} disabled={savingLine}
                  style={{flex:1, height:38, padding:'0 14px', border:'1px solid var(--border)', borderRadius:6, background:'#fafafa', color:'var(--text2)', fontWeight:700, fontSize:13, cursor:'pointer'}}>
                  취소
                </button>
                <button type="button" onClick={() => saveEditLine(it)} disabled={savingLine}
                  style={{flex:1, height:38, padding:'0 14px', border:'1px solid var(--accent)', borderRadius:6, background:'var(--accent)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer'}}>
                  {savingLine ? <span className="spinner"/> : '💾 저장'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
